import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// Query Keys
export const vendorKeys = {
  all: ['vendors'],
  lists: () => [...vendorKeys.all, 'list'],
  list: (filters) => [...vendorKeys.lists(), { filters }],
  details: () => [...vendorKeys.all, 'detail'],
  detail: (id) => [...vendorKeys.details(), id],
  pending: () => [...vendorKeys.all, 'pending'],
  stats: () => [...vendorKeys.all, 'stats'],
  services: (id) => [...vendorKeys.all, 'services', id],
  portfolio: (id) => [...vendorKeys.all, 'portfolio', id],
  reviews: (id) => [...vendorKeys.all, 'reviews', id],
  availability: (id) => [...vendorKeys.all, 'availability', id],
};

// API Functions
const vendorApi = {
  // Get all vendors with filters
  getVendors: async (params = {}) => {
    const response = await api.get('/vendors', { params });
    return response.data;
  },

  // Get vendor by ID
  getVendor: async (id) => {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  },

  // Search vendors
  searchVendors: async (query, params = {}) => {
    const response = await api.get('/vendors/search', {
      params: { q: query, ...params }
    });
    return response.data;
  },

  // Admin: Get pending vendor verifications
  getPendingVerifications: async (params = {}) => {
    const response = await api.get('/admin/vendors/pending', { params });
    return response.data;
  },

  // Admin: Verify vendor
  verifyVendor: async ({ id, data }) => {
    const response = await api.put(`/admin/vendors/${id}/verify`, data);
    return response.data;
  },

  // Admin: Reject vendor
  rejectVendor: async ({ id, data }) => {
    const response = await api.put(`/admin/vendors/${id}/reject`, data);
    return response.data;
  },

  // Admin: Update vendor profile
  updateVendorProfile: async ({ id, data }) => {
    const response = await api.put(`/admin/vendors/${id}/profile`, data);
    return response.data;
  },

  // Admin: Suspend or reactivate vendor
  suspendVendor: async ({ id, isActive, reason }) => {
    const response = await api.put(`/admin/vendors/${id}/suspend`, {
      is_active: isActive,
      reason: reason || null
    });
    return response.data;
  },

  // Admin: Get vendor verification details
  getVerificationDetails: async (id) => {
    const response = await api.get(`/admin/vendors/${id}/verification-details`);
    return response.data;
  },

  // Admin: Get verification statistics
  getVerificationStats: async () => {
    const response = await api.get('/admin/vendors/verification-stats');
    return response.data;
  },

  // Admin: Bulk verify vendors
  bulkVerifyVendors: async ({ vendorIds, data }) => {
    const response = await api.post('/admin/vendors/bulk-verify', {
      vendor_ids: vendorIds,
      ...data
    });
    return response.data;
  },

  // Get featured vendor services
  getFeaturedVendors: async (params = {}) => {
    const response = await api.get('/vendors/featured-services', { 
      params: { 
        per_page: 6,
        ...params 
      } 
    });
    return response.data;
  },

  // Public: Get vendor services for browse
  getPublicVendorServices: async (params = {}) => {
    const response = await api.get('/vendor-services', { params });
    return response.data;
  },

  // Get vendor services
  getVendorServices: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/services`);
    return response.data;
  },

  // Get single vendor service
  getVendorService: async (serviceId) => {
    const response = await api.get(`/vendor-services/${serviceId}`);
    return response.data;
  },

  // Get vendor portfolio
  getVendorPortfolio: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/portfolio`, { params });
    return response.data;
  },

  // Get vendor portfolio images (from user_images table)
  getVendorPortfolioImages: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/portfolio-images`);
    return response.data;
  },

  // Get vendor reviews
  getVendorReviews: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/reviews`, { params });
    return response.data;
  },

  // Get vendor availability
  getVendorAvailability: async (vendorId, params = {}) => {
    const response = await api.get(`/vendors/${vendorId}/availability`, { params });
    return response.data;
  },

  // Admin: Get all vendors (from users table)
  getAdminVendors: async (params = {}) => {
    const response = await api.get('/admin/vendors', { params });
    return response.data;
  },

  // Admin: Get vendor details (from users table)
  getAdminVendor: async (id) => {
    const response = await api.get(`/admin/vendors/${id}`);
    return response.data;
  },

  // Admin: Delete pending vendor (only unverified vendors)
  deletePendingVendor: async (id) => {
    const response = await api.delete(`/admin/vendors/${id}`);
    return response.data;
  }
};

// React Query Hooks

// Get featured vendors (Public)
export const useFeaturedVendors = (params = {}, options = {}) => {
  // Create a stable query key that includes location params for proper caching
  const queryKey = ['vendors', 'featured', params];
  
  return useQuery({
    queryKey: queryKey,
    queryFn: () => vendorApi.getFeaturedVendors(params),
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes (increased for better caching)
    cacheTime: 30 * 60 * 1000, // Cache for 30 minutes (increased to reduce API calls)
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce requests
    refetchOnMount: false, // Don't refetch on component mount if data exists in cache
    refetchOnReconnect: false, // Don't refetch on reconnect
    // Refetch when location params change
    enabled: options.enabled !== undefined ? options.enabled : true, // Allow disabling via options
    ...options
  });
};

// Get all vendors with filters (Admin)
export const useVendors = (params = {}, options = {}) => {
  return useQuery({
    queryKey: vendorKeys.list(params),
    queryFn: () => vendorApi.getAdminVendors(params),
    refetchInterval: 5000, // Refetch every 5 seconds to keep online status updated
    refetchIntervalInBackground: true, // Continue refetching even when tab is not active
    refetchOnWindowFocus: true, // Refetch when window gains focus
    staleTime: 2000, // Consider data stale after 2 seconds
    ...options
  });
};

// Get vendor by ID (Admin)
export const useVendor = (id, options = {}) => {
  return useQuery({
    queryKey: vendorKeys.detail(id),
    queryFn: () => vendorApi.getAdminVendor(id),
    enabled: !!id,
    ...options
  });
};

// Search vendors
export const useSearchVendors = (query, params = {}, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.lists(), 'search', { query, ...params }],
    queryFn: () => vendorApi.searchVendors(query, params),
    enabled: !!query,
    ...options
  });
};

// Admin: Get pending vendor verifications
export const usePendingVerifications = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.pending(), { params }],
    queryFn: () => vendorApi.getPendingVerifications(params),
    ...options
  });
};

// Admin: Get verification statistics
export const useVerificationStats = (options = {}) => {
  return useQuery({
    queryKey: vendorKeys.stats(),
    queryFn: vendorApi.getVerificationStats,
    ...options
  });
};

// Admin: Get vendor verification details
export const useVerificationDetails = (id, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.detail(id), 'verification'],
    queryFn: () => vendorApi.getVerificationDetails(id),
    enabled: !!id,
    ...options
  });
};

// Get vendor services
export const useVendorServices = (vendorId, options = {}) => {
  return useQuery({
    queryKey: vendorKeys.services(vendorId),
    queryFn: () => vendorApi.getVendorServices(vendorId),
    enabled: !!vendorId,
    ...options
  });
};

// Get single vendor service
export const useVendorService = (serviceId, options = {}) => {
  return useQuery({
    queryKey: ['vendor-service', serviceId],
    queryFn: () => vendorApi.getVendorService(serviceId),
    enabled: !!serviceId,
    ...options
  });
};

// Public: Get vendor services for browse
export const usePublicVendorServices = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['public-vendor-services', params],
    queryFn: () => vendorApi.getPublicVendorServices(params),
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

// Get vendor portfolio
export const useVendorPortfolio = (vendorId, params = {}, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.portfolio(vendorId), { params }],
    queryFn: () => vendorApi.getVendorPortfolio(vendorId, params),
    enabled: !!vendorId,
    ...options
  });
};

// Get vendor portfolio images (from user_images table)
export const useVendorPortfolioImages = (vendorId, options = {}) => {
  return useQuery({
    queryKey: ['vendor-portfolio-images', vendorId],
    queryFn: () => vendorApi.getVendorPortfolioImages(vendorId),
    enabled: !!vendorId,
    ...options
  });
};

// Get vendor reviews
export const useVendorReviews = (vendorId, params = {}, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.reviews(vendorId), { params }],
    queryFn: () => vendorApi.getVendorReviews(vendorId, params),
    enabled: !!vendorId,
    ...options
  });
};

// Get vendor availability
export const useVendorAvailability = (vendorId, params = {}, options = {}) => {
  return useQuery({
    queryKey: [...vendorKeys.availability(vendorId), { params }],
    queryFn: () => vendorApi.getVendorAvailability(vendorId, params),
    enabled: !!vendorId,
    ...options
  });
};

// Mutations

// Admin: Verify vendor
export const useVerifyVendor = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorApi.verifyVendor,
    onSuccess: (data, variables) => {
      // Invalidate and refetch vendor data
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorKeys.pending() });
      queryClient.invalidateQueries({ queryKey: vendorKeys.stats() });
    },
    ...options
  });
};

// Admin: Reject vendor
export const useRejectVendor = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorApi.rejectVendor,
    onSuccess: (data, variables) => {
      // Invalidate and refetch vendor data
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: vendorKeys.pending() });
      queryClient.invalidateQueries({ queryKey: vendorKeys.stats() });
    },
    ...options
  });
};

// Admin: Bulk verify vendors
export const useBulkVerifyVendors = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: vendorApi.bulkVerifyVendors,
    onSuccess: (data, variables) => {
      // Invalidate and refetch all vendor data
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
    ...options
  });
};

// Admin: Delete pending vendor
export const useDeletePendingVendor = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => vendorApi.deletePendingVendor(id),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
    ...options
  });
};

// Admin: Update vendor profile
export const useUpdateVendorProfile = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => vendorApi.updateVendorProfile({ id, data }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      options.onSuccess?.(data, variables);
    },
    ...options
  });
};

// Admin: Suspend vendor
export const useSuspendVendor = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive, reason }) => vendorApi.suspendVendor({ id, isActive, reason }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      options.onSuccess?.(data, variables);
    },
    ...options
  });
};

// Legacy service object for backward compatibility
export const vendorService = {
  getVendors: vendorApi.getVendors,
  getVendor: vendorApi.getVendor,
  searchVendors: vendorApi.searchVendors,
  getPendingVerifications: vendorApi.getPendingVerifications,
  verifyVendor: vendorApi.verifyVendor,
  rejectVendor: vendorApi.rejectVendor,
  getVerificationDetails: vendorApi.getVerificationDetails,
  getVerificationStats: vendorApi.getVerificationStats,
  bulkVerifyVendors: vendorApi.bulkVerifyVendors,
  getVendorServices: vendorApi.getVendorServices,
  getVendorPortfolio: vendorApi.getVendorPortfolio,
  getVendorReviews: vendorApi.getVendorReviews,
  getVendorAvailability: vendorApi.getVendorAvailability
};

export default vendorService;
